// Extensions de l'éditeur de texte (cours et analyses)
import { Extension, Node, mergeAttributes } from "@tiptap/core";
import Heading from "@tiptap/extension-heading";
import LinkExt from "@tiptap/extension-link";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { glossaryWord, termRegex } from "../../lib/glossary";
import { ReactNodeViewRenderer } from "@tiptap/react";
import FigureView from "./FigureView";

const shortId = () => Math.random().toString(36).slice(2, 8);

/** Encadré : bloc qui entoure un ou plusieurs paragraphes (orange dans les cours, violet dans les analyses). */
export const Callout = Node.create({
  name: "callout",
  group: "block",
  content: "block+",
  defining: true,
  parseHTML: () => [{ tag: "div[data-callout]" }],
  renderHTML: ({ HTMLAttributes }) => ["div", mergeAttributes(HTMLAttributes, { "data-callout": "true" }), 0],
});

/**
 * Titres avec un identifiant stable (id="t-xxxxxx") : sert d'ancre au sommaire.
 * L'identifiant est donné automatiquement à chaque nouveau titre et ne change plus ensuite.
 * Les H1 (collés depuis Google Docs, anciens textes) deviennent des H2 : ils comptent ainsi dans le sommaire.
 */
export const HeadingWithId = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      id: { default: null, parseHTML: (el) => el.getAttribute("id"), renderHTML: (a) => (a.id ? { id: a.id } : {}) },
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("headingIds"),
        appendTransaction: (trs, _old, state) => {
          if (!trs.some((t) => t.docChanged)) return null;
          const tr = state.tr;
          const seen = new Set();
          let changed = false;
          state.doc.descendants((node, pos) => {
            if (node.type.name !== "heading") return;
            let id = node.attrs.id;
            // Le titre de la page est déjà le seul « Titre 1 » : un H1 dans le texte devient un intertitre (H2)
            const level = node.attrs.level === 1 ? 2 : node.attrs.level;
            if (!id || seen.has(id) || level !== node.attrs.level) {
              if (!id || seen.has(id)) id = `t-${shortId()}`;
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, id, level });
              changed = true;
            }
            seen.add(id);
          });
          return changed ? tr : null;
        },
      }),
    ];
  },
});

/**
 * Lien : garde en plus
 * - glossary : l'identifiant d'une définition du glossaire (bouton « Glossaire ») ;
 * - internal : « course:id » ou « article:id » pour un lien vers un autre contenu du site (bouton « Lien interne »).
 *   L'adresse est recalculée à l'affichage : le lien reste valable si le contenu est renommé.
 */
export const SiteLink = LinkExt.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      glossary: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-glossary"),
        renderHTML: (a) => (a.glossary ? { "data-glossary": a.glossary } : {}),
      },
      internal: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-internal"),
        renderHTML: (a) => (a.internal ? { "data-internal": a.internal } : {}),
      },
    };
  },
});

/**
 * Image avec légende, source, largeur (en % de la colonne de texte) et alignement.
 * Les anciennes images (<img> seule) sont reprises automatiquement.
 */
export const Figure = Node.create({
  name: "figure",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: "" },
      caption: { default: "" },
      source: { default: "" },
      width: { default: 100 },
      align: { default: "center" }, // center | left | right
    };
  },
  parseHTML() {
    return [
      {
        tag: "figure[data-figure]",
        getAttrs: (el) => {
          const img = el.querySelector("img");
          return {
            src: img?.getAttribute("src"),
            alt: img?.getAttribute("alt") || "",
            caption: el.querySelector("[data-caption]")?.textContent || "",
            source: el.querySelector("[data-source]")?.textContent || "",
            width: parseInt(el.getAttribute("data-width") || "100", 10) || 100,
            align: el.getAttribute("data-align") || "center",
          };
        },
      },
      {
        tag: "img[src]",
        getAttrs: (el) => {
          const w = (el.style.width || "").match(/(\d+)%/);
          return { src: el.getAttribute("src"), alt: el.getAttribute("alt") || "", width: w ? parseInt(w[1], 10) : 100 };
        },
      },
    ];
  },
  renderHTML({ node }) {
    const { src, alt, caption, source, width, align } = node.attrs;
    const cap = [];
    if (caption) cap.push(["span", { "data-caption": "" }, caption]);
    if (source) cap.push(["span", { "data-source": "" }, source]);
    return [
      "figure",
      { "data-figure": "", "data-align": align, "data-width": String(width), style: `width:${width}%` },
      ["img", { src, alt: alt || caption || "" }],
      ...(cap.length ? [["figcaption", {}, ...cap]] : []),
    ];
  },
  addNodeView() {
    // Les champs légende / source de l'image gardent le clavier pour eux
    return ReactNodeViewRenderer(FigureView, { stopEvent: ({ event }) => /^(INPUT|TEXTAREA)$/.test(event.target?.tagName || "") });
  },
  addCommands() {
    return {
      setFigure:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    };
  },
});

// Mots du glossaire détectés automatiquement : soulignés dans l'éditeur (affichage seulement, rien n'est
// enregistré dans le texte). Même règle que sur le site : première apparition de chaque mot, jamais dans
// un titre ni dans un lien, et pas les mots décochés dans le bloc « Mots du glossaire ».
export const glossaryKey = new PluginKey("glossaryHighlight");
export const GlossaryHighlight = Extension.create({
  name: "glossaryHighlight",
  addOptions() {
    return { getGlossary: () => ({ terms: [], off: [] }) };
  },
  addProseMirrorPlugins() {
    const getGlossary = this.options.getGlossary;
    const build = (doc) => {
      const { terms, off } = getGlossary();
      if (!terms?.length) return DecorationSet.empty;
      const chunks = [];
      const manual = new Set();
      doc.descendants((node, pos) => {
        if (node.type.name === "heading" || node.type.name === "codeBlock") return false;
        if (!node.isText) return true;
        const link = node.marks.find((m) => m.type.name === "link");
        if (link) { if (link.attrs.glossary) manual.add(link.attrs.glossary); return false; }
        if (!node.marks.some((m) => m.type.name === "code")) chunks.push({ text: node.text, pos });
        return false;
      });
      const offSet = new Set(off || []);
      const claimed = [];
      const free = (a, b) => claimed.every(([x, y]) => b <= x || a >= y);
      const decos = [];
      terms
        .filter((t) => !manual.has(t.id))
        .map((t) => ({ t, word: glossaryWord(t.title) }))
        .filter((x) => x.word.length >= 2)
        .sort((a, b) => b.word.length - a.word.length)
        .forEach(({ t, word }) => {
          const re = termRegex(word, "giu");
          for (const c of chunks) {
            re.lastIndex = 0;
            let m, hit = null;
            while ((m = re.exec(c.text))) {
              const from = c.pos + m.index, to = from + m[0].length;
              if (free(from, to)) { hit = [from, to]; break; }
            }
            if (!hit) continue;
            if (!offSet.has(t.id)) {
              claimed.push(hit);
              decos.push(Decoration.inline(hit[0], hit[1], { class: "gloss-auto", title: `Glossaire : ${t.title}` }));
            }
            break; // seulement la première apparition
          }
        });
      return DecorationSet.create(doc, decos);
    };
    return [
      new Plugin({
        key: glossaryKey,
        state: {
          init: (_, { doc }) => build(doc),
          apply: (tr, old) => (tr.docChanged || tr.getMeta(glossaryKey) ? build(tr.doc) : old),
        },
        props: { decorations: (state) => glossaryKey.getState(state) },
      }),
    ];
  },
});

// Coupure d'aperçu gratuit : un repère déplaçable qui sépare l'aperçu du contenu réservé aux abonnés
export const PreviewBreak = Node.create({
  name: "previewBreak",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  parseHTML() {
    return [{ tag: "div[data-preview-break]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-preview-break": "true" })];
  },
});

export const PREVIEW_BREAK_RE = /<div[^>]*data-preview-break[^>]*>\s*<\/div>/gi;
